import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MessageCircle, ShoppingBag, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useMeta, pkr, waLink } from "@/lib/site";
import { Overline, Reveal } from "@/components/Reveal";

export default function Decants() {
  useMeta("The Discovery Set — Decants", "The Bonheur Eternal 3-piece discovery set: Breeze, Diva and Elysian in travel decants. The considered way to find your signature.");
  const { add } = useCart();
  const { data: d } = useQuery({ queryKey: ["decants"], queryFn: async () => (await api.get("/decants")).data });
  const { data: fragrances = [] } = useQuery({ queryKey: ["fragrances"], queryFn: async () => (await api.get("/fragrances")).data });
  const pieces = fragrances.filter((f) => (d?.slugs || ["breeze", "diva", "elysian"]).includes(f.slug));
  const [selected, setSelected] = useState([]);
  const toggle = (slug) => setSelected((prev) => {
    if (prev.includes(slug)) return prev.filter((s) => s !== slug);
    if (prev.length >= 3) { toast("Three decants make a box — remove one to swap."); return prev; }
    return [...prev, slug];
  });
  const addCustomSet = () => {
    const slugs = [...selected].sort();
    const names = slugs.map((s) => fragrances.find((f) => f.slug === s)?.name).join(" · ");
    add({ slug: `set-${slugs.join("+")}`, name: `Discovery Set — ${names} (3 × 5ml)`, price: d?.price || 1000, image: d?.images?.[slugs[0]] });
    toast.success(`Your Discovery Box (${names}) is in the cart.`);
    setSelected([]);
  };

  return (
    <div className="pt-32 md:pt-44 pb-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Overline>Decants</Overline>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2B2118] mt-4 tracking-tight">
              {d?.title || "The Discovery Set"}
            </h1>
            <p className="mt-6 text-[#5A4C3C] leading-loose max-w-xl">{d?.intro}</p>
            <div className="mt-8 flex flex-wrap gap-4" data-testid="decants-pricing-strip">
              <div className="border border-[#8F6B3A]/40 px-6 py-4">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#6E6151]">Single Decant · 5ml</p>
                <p className="font-display text-2xl font-semibold text-[#6F5433] mt-1">{pkr(d?.single_price || 400)} <span className="text-sm font-sans font-medium text-[#5C4A33]">each</span></p>
              </div>
              <div className="border border-[#8F6B3A]/40 px-6 py-4 bg-[#8F6B3A]/[0.06]">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#6E6151]">Discovery Set · 3 Decants</p>
                <p className="font-display text-2xl font-semibold text-[#6F5433] mt-1">{pkr(d?.price || 1000)} <span className="text-sm font-sans font-medium text-[#5C4A33]">for all 3</span></p>
              </div>
            </div>
          </Reveal>
          {d?.image && (
            <Reveal delay={0.15}>
              <div className="overflow-hidden border border-[#2B2118]/[0.07]">
                <img src={d.image} alt="Bonheur Eternal decants" className="w-full object-cover" data-testid="decants-hero-image" />
              </div>
            </Reveal>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-20">
          {pieces.map((f, i) => (
            <Reveal key={f.slug} delay={i * 0.12}>
              <Link to={`/fragrance/${f.slug}`} data-testid={`decant-piece-${f.slug}`} className="group block border border-[#2B2118]/[0.07] hover:border-[#8F6B3A]/40 transition-colors duration-500 bg-[#DDD3C1]/60 h-full overflow-hidden">
                {d?.images?.[f.slug] && (
                  <div className="overflow-hidden">
                    <img src={d.images[f.slug]} alt={`${f.name} decant`} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                  </div>
                )}
                <div className="p-8">
                  <div className="w-10 h-[3px] mb-6" style={{ background: f.hex }} />
                  <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: f.hex }}>5ml decant</p>
                  <h3 className="font-display text-2xl text-[#2B2118] mt-2 group-hover:text-[#8F6B3A] transition-colors">{f.name}</h3>
                  <p className="text-sm text-[#6E6151] mt-1">{f.tagline}</p>
                  <p className="text-sm font-medium text-[#6F5433] mt-4">Included in the {d ? pkr(d.price) : "PKR 1,000"} set · {d ? pkr(d.single_price || 400) : "PKR 400"} on its own</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-24 max-w-3xl">
          <h2 className="font-display text-2xl md:text-3xl text-[#2B2118] tracking-tight">What is a decant?</h2>
          <p className="mt-5 text-[#5A4C3C] leading-loose">{d?.what_is}</p>
        </Reveal>

        <Reveal className="mt-20 border border-[#8F6B3A]/25 p-10 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-8 bg-[#DDD3C1]">
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#8F6B3A]">3-Piece Discovery Set — 3 × 5ml Decants</p>
            <p className="font-display text-3xl md:text-4xl font-semibold text-[#6F5433] mt-3">{d ? pkr(d.price) : ""} <span className="text-lg font-sans font-medium text-[#5C4A33]">for the set of 3</span></p>
            <p className="text-sm text-[#6E6151] mt-2">Breeze · Diva · Elysian — one price, three decants. Delivered all over Pakistan.</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <button
              data-testid="decants-add-to-cart-btn"
              onClick={() => { add({ slug: "discovery-set", name: `${d?.title || "Discovery Set"} (${d?.size || "3 × 5ml"})`, price: d?.price || 0, image: d?.image }); toast.success("Discovery Set added to your cart."); }}
              className="inline-flex items-center gap-3 rounded-full bg-[#8F6B3A] text-[#FBF8F1] px-10 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#6F5433] transition-colors duration-300 w-fit"
            >
              <ShoppingBag size={16} strokeWidth={1.5} /> Add to Cart
            </button>
            <a
              href={waLink(`Hello Bonheur Eternal, I'd like to order the 3-piece Discovery Set (Breeze, Diva, Elysian) — ${d ? pkr(d.price) : ""}.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="decants-whatsapp-btn"
              className="inline-flex items-center gap-3 rounded-full border border-[#8F6B3A]/50 text-[#8F6B3A] px-10 py-4 text-xs tracking-[0.2em] uppercase hover:bg-[#8F6B3A] hover:text-[#FBF8F1] transition-colors duration-300 w-fit"
            >
              <MessageCircle size={16} strokeWidth={1.5} /> Order via WhatsApp
            </a>
          </div>
        </Reveal>

        <Reveal className="mt-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
            <div>
              <Overline>Single Decants &amp; Your Own Box</Overline>
              <h2 className="font-display text-2xl md:text-3xl text-[#2B2118] tracking-tight mt-3">Buy any decant for {d ? pkr(d.single_price || 400) : "PKR 400"} — or pick any 3 for {d ? pkr(d.price || 1000) : "PKR 1,000"}.</h2>
            </div>
          </div>
          <p className="text-sm text-[#6E6151] mb-10">Tap a card to select it for your own Discovery Box, or use "Add" to buy it on its own.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5" data-testid="single-decants-list">
            {fragrances.map((f) => {
              const isSel = selected.includes(f.slug);
              return (
                <div
                  key={f.slug}
                  data-testid={`single-decant-${f.slug}`}
                  onClick={() => toggle(f.slug)}
                  className={`cursor-pointer border transition-all duration-300 overflow-hidden bg-[#DDD3C1]/60 ${isSel ? "border-[#8F6B3A] ring-2 ring-[#8F6B3A]/40" : "border-[#2B2118]/[0.08] hover:border-[#8F6B3A]/40"}`}
                >
                  <div className="relative">
                    {d?.images?.[f.slug] && (
                      <img src={d.images[f.slug]} alt={`${f.name} 5ml decant`} loading="lazy" className="w-full object-cover" />
                    )}
                    <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center border transition-colors ${isSel ? "bg-[#8F6B3A] border-[#8F6B3A] text-[#FBF8F1]" : "bg-black/30 border-white/40 text-white/70"}`}>
                      <Check size={14} strokeWidth={2.5} />
                    </div>
                  </div>
                  <div className="p-4 flex items-center justify-between gap-2">
                    <div>
                      <p className="font-display text-lg text-[#2B2118]">{f.name}</p>
                      <p className="text-sm font-medium text-[#6F5433]">{pkr(d?.single_price || 400)} · 5ml</p>
                    </div>
                    <button
                      data-testid={`decant-add-${f.slug}`}
                      onClick={(e) => { e.stopPropagation(); add({ slug: `decant-${f.slug}`, name: `${f.name} — 5ml Decant`, price: d?.single_price || 400, image: d?.images?.[f.slug] || f.image }); toast.success(`${f.name} decant added to your cart.`); }}
                      className="rounded-full border border-[#8F6B3A]/40 text-[#8F6B3A] px-4 py-2 text-[10px] tracking-[0.2em] uppercase hover:bg-[#8F6B3A] hover:text-[#FBF8F1] transition-colors duration-300 shrink-0"
                    >
                      Add
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="sticky bottom-4 mt-10 border border-[#8F6B3A]/40 bg-[#DDD3C1]/95 backdrop-blur p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5 shadow-[0_10px_40px_rgba(43,33,24,0.15)]" data-testid="custom-box-bar">
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-[#8F6B3A]">Your Discovery Box — pick any 3</p>
              <p className="font-display text-xl text-[#2B2118] mt-1" data-testid="custom-box-selection">
                {selected.length === 0 ? "No decants selected yet" : selected.map((s) => fragrances.find((f) => f.slug === s)?.name).join(" · ")}
                <span className="text-sm text-[#6E6151] ml-2">({selected.length}/3)</span>
              </p>
            </div>
            <button
              data-testid="custom-box-add-btn"
              onClick={addCustomSet}
              disabled={selected.length !== 3}
              className="rounded-full bg-[#8F6B3A] text-[#FBF8F1] px-10 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#6F5433] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed w-fit"
            >
              Add My Box — {d ? pkr(d.price || 1000) : "PKR 1,000"}
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
